import uuidv4 from 'uuid/v4';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { EdmundsAPI } from 'client/data/api/api-client';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { isEstimatedTmvConditions } from 'site-modules/shared/components/estimated-values/estimated-values-utils';
import { validateZipCode } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { filterFleetTrims } from 'site-modules/shared/utils/core-page/fleet-trims';
import { VisitorModel } from './visitor';
import { getSupportedGraphqlModelYear } from './vehicle-model-year';

const Condition = PropTypes.shape({
  baseMSRP: PropTypes.number,
  baseInvoice: PropTypes.number,
  deliveryCharges: PropTypes.number,
  tmv: PropTypes.number,
  usedTmvRetail: PropTypes.number,
  usedPrivateParty: PropTypes.number,
  usedTradeIn: PropTypes.number,
  estimateTmv: PropTypes.number,
  tmvRecommendedRating: PropTypes.number,
});

const Tmv = PropTypes.shape({
  CLEAN: Condition,
  AVERAGE: Condition,
  OUTSTANDING: Condition,
  ROUGH: Condition,
});

const Style = PropTypes.shape({
  styleId: PropTypes.number,
  styleName: PropTypes.string,
  tmv: PropTypes.shape({
    conditions: Tmv,
  }),
});

const Styles = PropTypes.arrayOf(Style);

export const MmyStyle = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  trimName: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  makeName: PropTypes.string.isRequired,
  modelName: PropTypes.string.isRequired,
  makeSlug: PropTypes.string.isRequired,
  modelSlug: PropTypes.string.isRequired,
});

export const MmyStyles = PropTypes.arrayOf(MmyStyle);

export const AppraisalEntities = {
  Styles,
  Style,
  Tmv,
  Condition,
  MmyStyle,
  MmyStyles,
};

export const VEHICLE_MILEAGE_PER_YEAR = 12000;

export function buildAllStylesAppraisalPath({ make, model, year }) {
  return `makes["${make}"].models["${model}"].years["${year}"].styles.allconditionstmvs`;
}
export function buildAllStylesAppraisalFilteredPath({ make, model, year }) {
  return `makes["${make}"].models["${model}"].years["${year}"].styles.allconditionstmvsFiltered`;
}
export const buildStylesPath = ({ make, model, year }) =>
  make && model && year ? `makes["${make}"].models["${model}"].years["${year}"].styles` : null;

export const AppraisalModel = createModelSegment('appraisal', [
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].styles.allconditionstmvs',
    async resolve(match, context) {
      const { make, model, year } = match;
      const { zipCode } = await context.resolveValue('location', VisitorModel, false);
      const currentYear = new Date().getFullYear();
      const mileage = currentYear > year ? VEHICLE_MILEAGE_PER_YEAR * (currentYear - year) : 0;
      const zipCodeValid = validateZipCode(zipCode);

      const queryParams = objectToQueryString({
        ...(zipCodeValid ? { zipcode: zipCode } : {}),
        mileage,
      });

      const url = `/usedtmv/v3/makes/${make}/models/${model}/years/${year}/styles/allconditionstmvs?${queryParams}`;
      let appraisalData;
      try {
        appraisalData = await withMetrics(EdmundsAPI, context).fetchJson(url);
      } catch {
        appraisalData = {
          results: [],
        };
      }
      return appraisalData.results;
    },
  },
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].styles.allconditionstmvsFiltered',
    async resolve(match, context) {
      const appraisalStyles = await context.resolveValue(buildAllStylesAppraisalPath(match));
      return appraisalStyles
        .filter(appraisalStyle => isEstimatedTmvConditions(appraisalStyle))
        .toSorted((a, b) => a.styleName.localeCompare(b.styleName));
    },
  },
  {
    path: 'isAppraiseFinished',
  },
  {
    path: 'autoCompleteFilter',
  },
  {
    path: 'autoCompleteData',
    async resolve(match, context) {
      const text = await context.resolveValue('autoCompleteFilter');

      let results = null;

      if (!text) {
        return {
          results,
        };
      }

      try {
        results = await withMetrics(EdmundsAPI, context).fetchJson(
          `/autocomplete/v1/vehicle?type=NEW,NEW_USED,USED&text=${text}`
        );
      } catch {
        results = [];
      }

      return {
        id: uuidv4(),
        results,
      };
    },
  },
  /**
   * @see buildStylesPath()
   */
  {
    path: 'makes["{make}"].models["{model}"].years["{year}"].styles',
    async resolve(match, context) {
      const { make, model, year } = match;
      const numericYear = getSupportedGraphqlModelYear(year);
      if (numericYear === null) {
        return [];
      }

      let styles;

      try {
        const response = await withMetrics(EdmundsGraphQLFederation, context).query(
          gql`
            query ($makeSlug: String!, $modelSlug: String!, $year: Int!) {
              modelYears(makeSlug: $makeSlug, modelSlug: $modelSlug, year: $year) {
                makeName
                modelName
                styleList(sortBy: { shareBySales: DESC }) {
                  styles {
                    name
                    id
                    trimName
                  }
                }
              }
            }
          `,
          {
            makeSlug: make,
            modelSlug: model,
            year: numericYear,
          }
        );
        const { makeName, modelName } = response.modelYears[0];
        styles = response.modelYears[0].styleList.styles.map(style => ({
          ...style,
          year: numericYear,
          makeName,
          modelName,
          makeSlug: make,
          modelSlug: model,
        }));
      } catch {
        styles = [];
      }

      return filterFleetTrims(styles, context);
    },
  },
]);
